import { css } from "@linaria/core"
import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"
import { useState } from "react"


import { Badge } from "@/components/Badge/Badge.tsx"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemLike,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from "@/components/DropdownMenu/DropdownMenu.tsx"
import { Ellipsis, EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { Spring } from "@/components/Flex.ts"
import { PromptInputStyles } from "@/components/PromptInput/PromptInputStyles.ts"
import { Scrollable } from "@/components/Scrollable/Scrollable.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { ChevronRightLight, ClaudeLogoFill, Icon20, OpenaiLogoFill } from "@/icons/Icons.ts"

import type { LLMSelection } from "@/components/LLMSelector/LLMSelection.ts"
import type { GrazieLifeCycleDTO, GrazieLLMProfileDTO } from "@/lib/client/generated/TypeDefinitions.ts"

// Primary models to show on the top level
const PRIMARY_MODELS = [
  "GPT-5",
  "Claude 4.5 Sonnet"
]

export const LLMSelector = observer(({ llmSelection, devMode }: {
  llmSelection: LLMSelection | null
  devMode?: boolean
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [subDropdownOpen, setSubDropdownOpen] = useState(false)

  if (!llmSelection) {
    return (
      <Tooltip content="LLM selection is not available">
        <PromptInputStyles.FooterIconContainer aria-disabled={true}>
          <ClaudeLogoFill className={ModelIcon} />
        </PromptInputStyles.FooterIconContainer>
      </Tooltip>
    )
  }

  const nonDeprecatedModels = llmSelection.availableModels.filter(model => !model.lifeCycle?.deprecated)
  const primaryModels = nonDeprecatedModels.filter(model => isPrimaryModel(model.fullName))
  const secondaryModels = nonDeprecatedModels.filter(model => !isPrimaryModel(model.fullName))

  primaryModels.sort((a, b) => removeVendorPrefix(a.fullName).localeCompare(removeVendorPrefix(b.fullName)))
  secondaryModels.sort((a, b) => removeVendorPrefix(a.fullName).localeCompare(removeVendorPrefix(b.fullName)))

  const selectedModel = llmSelection.availableModels.find(m => m.id === llmSelection.selectedLlmId.get())
  const displayName = selectedModel ? removeVendorPrefix(selectedModel.fullName) : "Choose LLM"

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <Tooltip content={displayName}>
        <DropdownMenuTrigger>
          <ModelName selectedModel={selectedModel} displayName={displayName} />
        </DropdownMenuTrigger>
      </Tooltip>

      <StyledDropdownMenuContent
        side="top"
        onCloseAutoFocus={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.stopPropagation()}
      >
        <Scrollable>
          {primaryModels.map(llm => (
            <LlmDropdownItem
              key={llm.id}
              onClick={() => {
                llmSelection.setSelectedLlmId(llm.id)
                setDropdownOpen(false)
              }}
            >
              <span>{removeVendorPrefix(llm.fullName)}</span>
              <Spring />
              <LLMBadges lifecycle={llm.lifeCycle} />
            </LlmDropdownItem>
          ))}

          {devMode && secondaryModels.length > 0 && (
            <DropdownMenuSub open={subDropdownOpen} onOpenChange={setSubDropdownOpen}>
              <DropdownMenuSubTrigger
                asChild
                onClick={(e) => {
                  e.preventDefault()
                  setSubDropdownOpen(true)
                }}
              >
                <MoreModelsItem>
                  <span>More models</span>
                  <Spring />
                  <ChevronIcon>
                    <ChevronRightLight className={Icon20} />
                  </ChevronIcon>
                </MoreModelsItem>
              </DropdownMenuSubTrigger>

              <StyledDropdownMenuSubContent sideOffset={20} onEscapeKeyDown={(e) => e.stopPropagation()}>
                <Scrollable>
                  {secondaryModels.map(llm => (
                    <LlmDropdownItem
                      key={llm.id}
                      onClick={() => {
                        llmSelection.setSelectedLlmId(llm.id)
                        setDropdownOpen(false)
                      }}
                    >
                      <span>{removeVendorPrefix(llm.fullName)}</span>
                      <Spring />
                      <LLMBadges lifecycle={llm.lifeCycle} />
                    </LlmDropdownItem>
                  ))}
                </Scrollable>
              </StyledDropdownMenuSubContent>
            </DropdownMenuSub>
          )}
        </Scrollable>
      </StyledDropdownMenuContent>
    </DropdownMenu>
  )
})

const ModelName = ({ selectedModel, displayName }: {
  selectedModel: GrazieLLMProfileDTO | undefined
  displayName: string
}) => {
  if (selectedModel?.fullName.includes("Anthropic")) {
    return (
      <PromptInputStyles.FooterIconContainer>
        <ClaudeLogoFill className={ModelIcon} />
      </PromptInputStyles.FooterIconContainer>
    )
  } else if (selectedModel?.fullName.includes("OpenAI")) {
    return (
      <PromptInputStyles.FooterIconContainer>
        <OpenaiLogoFill className={ModelIcon} />
      </PromptInputStyles.FooterIconContainer>
    )
  } else {
    return <EllipsisContainer><Ellipsis>{displayName}</Ellipsis></EllipsisContainer>
  }
}

const LLMBadges = (props: {
  lifecycle: GrazieLifeCycleDTO | null
}) => {
  if (!props.lifecycle) {
    return null
  }

  return (
    <>
      {props.lifecycle.deprecated && <Badge kind="warning">Deprecated</Badge>}
      {props.lifecycle.experimental && <Badge kind="info">Experimental</Badge>}
      {props.lifecycle.preview && <Badge kind="success">Preview</Badge>}
    </>
  )
}

function removeVendorPrefix(fullName: string): string {
  const vendorPrefixes = [
    "Anthropic",
    "Alibaba",
    "Google",
    "OpenAI",
  ]

  for (const prefix of vendorPrefixes) {
    if (fullName.startsWith(prefix)) {
      return fullName.substring(prefix.length).trim()
    }
  }

  return fullName
}

function isPrimaryModel(modelName: string): boolean {
  return PRIMARY_MODELS.includes(removeVendorPrefix(modelName))
}

const ChevronIcon = styled.div`
  display: flex;
  color: var(--secondary-text-color);
`

const StyledDropdownMenuContent = styled(DropdownMenuContent)`
  min-width: var(--radix-dropdown-menu-trigger-width, 150px);
  max-height: 300px;
`

const StyledDropdownMenuSubContent = styled(DropdownMenuSubContent)`
  min-width: var(--radix-dropdown-menu-trigger-width, 150px);
  max-height: 300px;
`

const LlmDropdownItem = styled(DropdownMenuItem)`
  display: flex;
  align-items: center;
  gap: 4px;

  cursor: pointer;
  white-space: nowrap;
`

const MoreModelsItem = styled(DropdownMenuItemLike)`
  display: flex;
  align-items: center;
  gap: 4px;

  padding: 4px 8px !important;
  white-space: nowrap;
  cursor: pointer;

  &:focus-visible {
    outline: none !important;
  }
`

const ModelIcon = css`
  width: 20px;
  height: 20px;

  color: var(--secondary-text-color);
`
